import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {getVisualizationConfigGQLFromStep} from 'analysis-tool/data/StepConfigUtils';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  GetRandomSubsetDataFrame,
  GetRandomSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetRandomSubsetDataFrame';
import {
  DEFAULT_DATA_FRAME_ROW_LIMIT,
  GET_RANDOM_SUBSET_DATA_FRAME_QUERY,
} from 'data-frames/graphql/GetDataFrame';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import {VisualizationConfig} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {DataFramePopoverContents} from './DataFramePopover';
import {MAX_POPOVER_HEIGHT, MAX_POPOVER_WIDTH} from './PopoverConstants';

interface ChartPopoverContentsProps {
  visualizationConfig: VisualizationConfig;
  visualizationName: string;
  dataFrameId: string;
}

/**
 * Popover contents for a chart visualization.
 *
 * If there are >1,000 rows, 1,000 rows are randomly sampled.
 *
 * To render a table visualization, see `DataFramePopoverContents`, which has a dynamic height and does not
 * sample points.
 *
 * @package
 */
function ChartPopoverContents({
  visualizationConfig,
  visualizationName,
  dataFrameId,
}: ChartPopoverContentsProps): JSX.Element {
  return (
    <BenchlingQuery<GetRandomSubsetDataFrame, GetRandomSubsetDataFrameVariables>
      query={GET_RANDOM_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          <div className={css(styles.floatContent)}>
            <PresentationalUniversalBlockView
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              inputDataFrame={dataFrame.randomSubsetDataFrameJson}
              visualizationConfig={visualizationConfig}
              viewStateId={`analysis-flowchart-chart-popover-${dataFrameId}`}
              paneProps={{title: visualizationName, toolbar: <></>}}
            />
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

/**
 * Popover contents for a VISUALIZATION step.
 * @package
 */
export default function VisualizationPopoverContents({step}: {step: GetAnalysisPipelineStep}): JSX.Element {
  assert(step.definition.stepType === PipelineStepType.VISUALIZATION, 'Expected VISUALIZATION step');
  const {visualizationConfig, visualizationName} = getVisualizationConfigGQLFromStep(step);
  const visualizationType = getVisualizationType(visualizationConfig);
  const dataFrameId = step.inputStepDatas[0].dataFrames[0].id;

  return visualizationType === VisualizationType.TABLE ? (
    <DataFramePopoverContents dataFrameId={dataFrameId} />
  ) : (
    <ChartPopoverContents
      visualizationConfig={visualizationConfig}
      visualizationName={visualizationName}
      dataFrameId={dataFrameId}
    />
  );
}

const styles = StyleSheet.create({
  floatContent: {
    height: MAX_POPOVER_HEIGHT,
    width: MAX_POPOVER_WIDTH,
  },
});
